import { ref } from 'vue';

export interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
}

export interface Theme extends ThemeColors {
  dark?: ThemeColors;
}

const defaultTheme: Theme = {
  primary: 'var(--color-pink-xx)',
  secondary: 'var(--color-red-17)',
  text: '#2F2F2F',
};

// The branch colors land in `--brand-*`. Never write `--theme-*` here: an inline property on
// <html> beats every stylesheet rule, and color-modes.css derives `--theme-*` per color mode.
const BRAND_VARIABLES: Record<keyof ThemeColors, string> = {
  primary: '--brand-primary',
  secondary: '--brand-secondary',
  text: '--brand-text',
};

const DARK_SUFFIX = '-dark';

const COLOR_KEYS = Object.keys(BRAND_VARIABLES) as Array<keyof ThemeColors>;

const currentTheme = ref<Theme>(defaultTheme);

function useTheme() {
  const setTheme = (theme: Theme) => {
    currentTheme.value = theme;
    localStorage.setItem('userTheme', JSON.stringify(theme));

    const style = document.documentElement.style;
    COLOR_KEYS.forEach((key) => {
      style.setProperty(BRAND_VARIABLES[key], theme[key]);
      if (theme.dark) {
        style.setProperty(BRAND_VARIABLES[key] + DARK_SUFFIX, theme.dark[key]);
      } else {
        style.removeProperty(BRAND_VARIABLES[key] + DARK_SUFFIX);
      }
    });
  };

  const clearTheme = () => {
    currentTheme.value = defaultTheme;
    localStorage.removeItem('userTheme');

    const style = document.documentElement.style;
    COLOR_KEYS.forEach((key) => {
      style.removeProperty(BRAND_VARIABLES[key]);
      style.removeProperty(BRAND_VARIABLES[key] + DARK_SUFFIX);
    });
  };

  return {
    currentTheme,
    setTheme,
    clearTheme,
  };
}

export default useTheme;
